// Liste admin des équipes : filtres, création (modal), édition (fiche), suppression

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Loader2,
  Pencil,
  PlusCircle,
  RefreshCcw,
  Search,
  Trash2,
  ChevronRight,
  LayoutDashboard,
} from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";
import {
  AdminPageHero,
  AdminDataTableShell,
  AdminEmptyState,
  adminHeroActionClassName,
} from "../../modules/admin/ui";
import ModalEquipe from "../../components/ModalEquipe";
import ModalZoneQuickCreate from "../../components/ModalZoneQuickCreate";

type ZoneLite = { id: number; nom: string; actif?: boolean };

type EquipeRow = {
  id: number;
  nom: string;
  actif: boolean;
  capaciteJour: number;
  zone: ZoneLite | null;
  chef: { id: number; nom: string; prenom: string | null } | null;
  _count?: { agents: number; tournees: number };
  agents?: unknown[];
};

type EmptyReason = "zones-error" | "no-zones" | "no-equipes" | "no-match" | null;

const EquipesAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<EquipeRow[]>([]);
  const [zones, setZones] = useState<ZoneLite[]>([]);
  const [zonesError, setZonesError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterZoneId, setFilterZoneId] = useState<number | "">("");
  const [filterActif, setFilterActif] = useState<"" | "actif" | "inactif">("");
  const [modalOpen, setModalOpen] = useState(false);
  const [zoneModalOpen, setZoneModalOpen] = useState(false);
  const [agentsSansEquipe, setAgentsSansEquipe] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setZonesError(false);

    let zoneFetchFailed = false;

    try {
      const zRes = await api.get("/zones");
      const rawZ = Array.isArray(zRes.data) ? zRes.data : zRes.data?.data ?? [];
      setZones(
        (rawZ as { id: number; nom: string; actif?: boolean }[]).map((z) => ({
          id: z.id,
          nom: z.nom,
          actif: z.actif,
        }))
      );
    } catch {
      zoneFetchFailed = true;
      setZones([]);
      setZonesError(true);
      toast.warning(
        "Impossible de charger les zones. Vérifiez l’API ou créez une zone après correction.",
        { toastId: "zones-load-warn", autoClose: 6000 }
      );
    }

    try {
      const params =
        filterZoneId === "" ? {} : { params: { zoneId: Number(filterZoneId) } };
      const eqRes = await api.get("/equipes", params);
      const list = Array.isArray(eqRes.data) ? eqRes.data : [];
      setRows(list as EquipeRow[]);
    } catch {
      toast.error("Impossible de charger les équipes.");
      setRows([]);
    }

    if (!zoneFetchFailed) {
      try {
        const agRes = await api.get("/agents");
        const agentsArr = Array.isArray(agRes.data) ? agRes.data : [];
        setAgentsSansEquipe(
          agentsArr.filter(
            (a: { actif?: boolean; equipeId?: number | null }) =>
              a.actif && !a.equipeId
          ).length
        );
      } catch {
        setAgentsSansEquipe(null);
      }
    } else {
      setAgentsSansEquipe(null);
    }

    setLoading(false);
  }, [filterZoneId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (searchParams.get("openCreate") === "1") {
      setModalOpen(true);
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.delete("openCreate");
          return n;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const filtered = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    return rows.filter((e) => {
      const matchText =
        !q ||
        [e.nom, e.zone?.nom, e.chef?.nom, e.chef?.prenom]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));

      const matchActif =
        filterActif === ""
          ? true
          : filterActif === "actif"
            ? e.actif
            : !e.actif;

      return matchText && matchActif;
    });
  }, [rows, debouncedSearch, filterActif]);

  const kpiUnavailable = zonesError;

  const kpi = useMemo(() => {
    if (kpiUnavailable) {
      return {
        zonesActives: null as number | null,
        equipesActives: null as number | null,
        agentsAffectes: null as number | null,
        agentsSansEquipe: null as number | null,
        capaciteJour: null as number | null,
      };
    }
    const zonesActives = zones.filter((z) => z.actif !== false).length;
    const equipesActives = rows.filter((e) => e.actif).length;
    let agentsAffectes = 0;
    let capaciteJour = 0;
    for (const e of rows) {
      const n =
        e._count?.agents ??
        (Array.isArray(e.agents) ? e.agents.length : 0);
      agentsAffectes += n;
      capaciteJour += e.capaciteJour ?? 0;
    }
    return {
      zonesActives,
      equipesActives,
      agentsAffectes,
      agentsSansEquipe,
      capaciteJour,
    };
  }, [rows, zones, agentsSansEquipe, kpiUnavailable]);

  const emptyReason: EmptyReason = useMemo(() => {
    if (loading) return null;
    if (filtered.length > 0) return null;
    if (rows.length > 0) return "no-match";
    if (zonesError) return "zones-error";
    if (zones.length === 0) return "no-zones";
    return "no-equipes";
  }, [loading, filtered.length, zonesError, zones.length, rows.length]);

  const handleDelete = async (e: EquipeRow) => {
    if (
      !window.confirm(
        `Supprimer l’équipe « ${e.nom} » ? Les agents seront désaffiliés selon la règle métier.`
      )
    ) {
      return;
    }
    try {
      await api.delete(`/equipes/${e.id}`);
      toast.success("Équipe supprimée.");
      void load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      toast.error(ax.response?.data?.error || "Suppression impossible.");
    }
  };

  const canCreateEquipe = !zonesError && zones.length > 0;
  const newEquipeTitle = canCreateEquipe
    ? undefined
    : "Créez une zone avant de créer une équipe";

  const heroActions = (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        disabled={!canCreateEquipe}
        title={newEquipeTitle}
        className={adminHeroActionClassName(!canCreateEquipe)}
      >
        Nouvelle équipe
        <ChevronRight className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => setZoneModalOpen(true)}
        className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-600/50 bg-emerald-950/40 px-4 py-2.5 text-sm font-medium text-emerald-200 hover:bg-emerald-950/70"
      >
        <PlusCircle className="h-4 w-4" />
        Créer une zone
      </button>
      <Link
        to="/admin/agents"
        className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
      >
        Agents terrain
      </Link>
      <Link
        to="/admin/dashboard"
        className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
      >
        <LayoutDashboard className="h-4 w-4" />
        Tableau de bord
      </Link>
    </>
  );

  const kpiCells: [string, number | null][] = [
    ["Zones actives", kpi.zonesActives],
    ["Équipes actives", kpi.equipesActives],
    ["Agents affectés", kpi.agentsAffectes],
    ["Agents sans équipe", kpi.agentsSansEquipe],
    ["Capacité jour", kpi.capaciteJour],
  ];

  return (
    <div className="space-y-5 text-slate-100">
      <AdminPageHero
        eyebrow="Organisation terrain"
        title="Équipes"
        subtitle="Créer, filtrer, modifier ou supprimer une équipe. L’édition détaillée (membres, tournées) se fait sur la fiche équipe."
        actions={heroActions}
      />

      {!loading && zonesError ? (
        <div
          role="alert"
          className="rounded-2xl border border-amber-500/35 bg-amber-500/10 px-4 py-3 text-sm text-amber-100"
        >
          Impossible de charger les zones. Vérifiez l’API ou créez une zone après correction.
        </div>
      ) : null}

      <section className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {kpiCells.map(([label, val]) => (
          <div
            key={label}
            className="rounded-xl border border-slate-800 bg-slate-900/50 px-4 py-3"
          >
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              {label}
            </p>
            <p className="mt-1 text-2xl font-bold text-white tabular-nums">
              {val === null ? "—" : val}
            </p>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 shadow-lg backdrop-blur-sm md:p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="relative flex min-w-0 flex-1 items-center">
            <Search className="pointer-events-none absolute left-3 h-4 w-4 text-slate-500" />
            <input
              type="search"
              placeholder="Rechercher par nom, zone, chef…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full rounded-xl border border-slate-700 bg-slate-950/60 py-2.5 pl-10 pr-3 text-sm text-white outline-none ring-emerald-500/30 placeholder:text-slate-500 focus:border-emerald-500/50 focus:ring-2"
            />
          </div>
          <div className="flex flex-wrap gap-2 md:justify-end">
            <select
              value={filterZoneId === "" ? "" : String(filterZoneId)}
              onChange={(e) =>
                setFilterZoneId(e.target.value === "" ? "" : Number(e.target.value))
              }
              className="rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-white outline-none focus:border-emerald-500/50 focus:ring-2"
            >
              <option value="">Toutes les zones</option>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.nom}
                </option>
              ))}
            </select>
            <select
              value={filterActif}
              onChange={(e) =>
                setFilterActif(e.target.value as "" | "actif" | "inactif")
              }
              className="rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-white outline-none focus:border-emerald-500/50 focus:ring-2"
            >
              <option value="">Statut : tous</option>
              <option value="actif">Actives</option>
              <option value="inactif">Inactives</option>
            </select>
            <button
              type="button"
              onClick={() => void load()}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800"
            >
              <RefreshCcw className="h-4 w-4" />
              Actualiser
            </button>
          </div>
        </div>
      </section>

      {loading ? (
        <p className="flex items-center justify-center gap-2 rounded-2xl border border-slate-800 bg-slate-900/40 py-16 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
          Chargement…
        </p>
      ) : emptyReason === "zones-error" ? (
        <AdminDataTableShell>
          <AdminEmptyState
            title="Zones indisponibles"
            description="Le chargement des zones a échoué. La création d’équipe est désactivée tant que les zones ne sont pas disponibles."
            action={
              <button
                type="button"
                onClick={() => void load()}
                className={adminHeroActionClassName(false)}
              >
                <RefreshCcw className="h-4 w-4" />
                Réessayer
              </button>
            }
          />
        </AdminDataTableShell>
      ) : emptyReason === "no-zones" ? (
        <AdminDataTableShell>
          <AdminEmptyState
            title="Aucune zone créée"
            description="Créez d’abord une zone pour organiser les équipes, affecter les agents et planifier les tournées."
            action={
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setZoneModalOpen(true)}
                  className={adminHeroActionClassName(false)}
                >
                  <PlusCircle className="h-4 w-4" />
                  Créer une zone
                </button>
                <Link
                  to="/admin/agents"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-600 px-4 py-2.5 text-sm font-medium text-slate-200 hover:bg-slate-800"
                >
                  Voir agents terrain
                </Link>
              </div>
            }
          />
        </AdminDataTableShell>
      ) : emptyReason === "no-equipes" ? (
        <AdminDataTableShell>
          <AdminEmptyState
            title="Aucune équipe créée"
            description="Créez une équipe rattachée à une zone pour affecter des agents."
            action={
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                disabled={!canCreateEquipe}
                title={newEquipeTitle}
                className={adminHeroActionClassName(!canCreateEquipe)}
              >
                <PlusCircle className="h-4 w-4" />
                Créer une équipe
              </button>
            }
          />
        </AdminDataTableShell>
      ) : filtered.length === 0 ? (
        <AdminDataTableShell>
          <AdminEmptyState
            title="Aucun résultat"
            description="Ajustez les filtres ou la recherche."
          />
        </AdminDataTableShell>
      ) : (
        <AdminDataTableShell>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/80 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Équipe</th>
                  <th className="px-4 py-3">Zone</th>
                  <th className="px-4 py-3">Chef</th>
                  <th className="px-4 py-3 text-center">Agents</th>
                  <th className="px-4 py-3 text-center">Cap. jour</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/90">
                {filtered.map((e) => {
                  const nAgents =
                    e._count?.agents ??
                    (Array.isArray(e.agents) ? e.agents.length : 0);
                  return (
                    <tr
                      key={e.id}
                      className="bg-slate-900/20 transition hover:bg-slate-800/50"
                    >
                      <td className="px-4 py-3">
                        <span className="font-semibold text-emerald-200/95">{e.nom}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {e.zone?.nom ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-400">
                        {e.chef
                          ? `${e.chef.prenom ?? ""} ${e.chef.nom}`.trim()
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-300">{nAgents}</td>
                      <td className="px-4 py-3 text-center text-slate-300">
                        {e.capaciteJour ?? 0}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
                            e.actif
                              ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-200"
                              : "border-slate-600 bg-slate-800 text-slate-400"
                          }`}
                        >
                          {e.actif ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap justify-center gap-1">
                          <button
                            type="button"
                            title="Éditer la fiche"
                            className="rounded-lg border border-slate-600 bg-slate-800/80 p-2 text-sky-300 hover:border-sky-500/40"
                            onClick={() => navigate(`/admin/equipes/${e.id}`)}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            title="Supprimer"
                            className="rounded-lg border border-slate-600 bg-slate-800/80 p-2 text-rose-400 hover:border-rose-500/40"
                            onClick={() => void handleDelete(e)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="border-t border-slate-800 px-4 py-3 text-xs text-slate-500">
            {filtered.length} équipe(s) affichée(s)
            {filterZoneId !== "" ? " (filtrées par zone côté API)" : ""}.
          </p>
        </AdminDataTableShell>
      )}

      {modalOpen && (
        <ModalEquipe
          zones={zones}
          zonesLoadError={zonesError}
          onClose={() => setModalOpen(false)}
          onSuccess={() => void load()}
          onOpenZoneCreate={() => setZoneModalOpen(true)}
        />
      )}

      <ModalZoneQuickCreate
        open={zoneModalOpen}
        onClose={() => setZoneModalOpen(false)}
        onCreated={() => void load()}
      />
    </div>
  );
};

export default EquipesAdminPage;
